// Common domain constants: application shell, validation, environments and
// dashboard commands. User-visible copy lives in common/copy.js.

export const Application = {
  Application: "application",
  Website: "website",
  KeyManagementPriorities: {
    Suggestions: {
      //A list open under a box being typed in closes before whatever holds the box
      Priority: 200,
    },
    Modal: {
      //Close Modal First on Escape Key
      Priority: 150,
    },
    CommandPalette: {
      //Then the Command Palette
      Priority: 125,
    },
    SlideOver: {
      //Then the Slide Over
      Priority: 100,
    },
    OverlayPane: {
      //Then the Overlay
      Priority: 50,
    },
    ExpandedPane: {
      //Then a pane that has taken the whole window (components/Expandable)
      Priority: 25,
    },
    Feature: {
      //Then the Feature Containing All
      Priority: 0,
    },
  },
  StorageKeys: {
    LocalStorage: "Pellerex",
    SessionStorage: "Pellerex",
    MyOrganisations: "my-augmented-organisation-list",
    MyTenants: "my-tenant-list",
    // The Logs tab's recent searches, by product (local storage)
    LogSearches: "log-searches",
    // When this tab last reloaded itself to pick up a new build (session storage)
    StaleBuildReloadAt: "stale-build-reload-at",
    Navigation: {
      RedirectUrl: "redirect-url",
    },
  },
};

export const General = {
  // Standard debounce delays (ms). TypingMs for as-you-type work against our own
  // APIs (search, slug previews); ExternalCheckMs for checks that fan out to
  // external providers (name availability against GitHub/AzDO).
  Debounce: {
    TypingMs: 500,
    ExternalCheckMs: 1000,
  },
  // How many milliseconds make up each unit of time, for every sum that turns one unit into another.
  Time: {
    MsPerSecond: 1000,
    MsPerMinute: 60 * 1000,
    MsPerHour: 60 * 60 * 1000,
    MsPerDay: 24 * 60 * 60 * 1000,
  },
  PhoneValidationRegEx:
    /([0-9\s\-]{7,})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
  Validation: {
    RegExPatterns: {
      UUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
      // RFC 5322 flavoured email matcher, shared by every email field.
      EmailValidationRegEx:
        /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
      AlphaNumericPlusHyphenValidationRegEx: /^[a-zA-Z0-9-]+$/,
      // A plus sign anywhere in a value. Identity refuses one in the email of a new account
      // (RegisterViewModelValidator), so the sign-up form (screens/InternalUser/Auth/AuthForm.jsx)
      // refuses it first.
      PlusSignRegEx: /\+/,
      // A toggle's key: lower-case words and numbers joined by hyphens, as Feature Management takes it.
      ToggleKeyValidationRegEx: /^[a-z0-9]+(-[a-z0-9]+)*$/,
      // What separates the values of a list that is pasted or read from a file: a line end or a comma.
      ListValueSeparatorRegEx: /[\r\n,]+/,
      // The quotes a spreadsheet puts around a value when it writes a file.
      SurroundingQuotesRegEx: /^"(.*)"$/,
      AlphaNumericPlusDotValidationRegEx: /^[a-zA-Z0-9.]+$/,
      AlphaNumericPlusDotAndDashValidationRegEx: /^[a-zA-Z0-9.-]+$/,
      AlphaNumericValidationRegEx: /^[a-zA-Z0-9]+$/,
      // What a person may type between the digits of a large number ("2,000,000", "2 000 000"),
      // taken out by removeThousandsSeparators in common/sanitisation.js.
      ThousandsSeparatorsGlobalRegEx: /[,\s]/g,
      // Sanitisation counterparts (global match): everything OUTSIDE the
      // allowed set, plus run-collapsers and edge-trimmers, for the input
      // scrubbers in common/sanitisation.js.
      NotAlphaNumericGlobalRegEx: /[^a-zA-Z0-9]/g,
      NotAlphaNumericOrDotGlobalRegEx: /[^a-zA-Z0-9.]/g,
      NotAlphaNumericDotOrDashGlobalRegEx: /[^a-zA-Z0-9.-]/g,
      NotDnsSlugCharacterRunsGlobalRegEx: /[^a-z0-9-]+/g,
      RepeatedDotsGlobalRegEx: /\.{2,}/g,
      RepeatedDashesGlobalRegEx: /-{2,}/g,
      EdgeDashesGlobalRegEx: /^-+|-+$/g,
      TrailingDashesGlobalRegEx: /-+$/g,
      StartsWithLowerCaseLetterRegEx: /^[a-z]/,
      AlphaValidationRegEx: /^[a-zA-Z]+$/,
      LowerCaseAlphaValidationRegEx: /^[a-z]+$/,
      // Typed search (components/Search/typedFilters.js): how a filter word is written
      // ("status:5xx", "-type:trace") and how a value goes into double quotes and comes back
      // out of them. What writes typed search and what reads it share these.
      TypedFilterWordRegEx: /^(-?)([a-z]+):([\s\S]+)$/,
      TypedFilterStartRegEx: /^-?([a-z]+):/,
      NeedsDoubleQuotesRegEx: /[\s,"]/,
      WhitespaceRegEx: /\s/,
      // Splitting at this keeps the runs of whitespace as items of their own.
      WhitespaceRunsSplitRegEx: /(\s+)/,
      CommaRegEx: /,/,
      BackslashGlobalRegEx: /\\/g,
      DoubleQuoteGlobalRegEx: /"/g,
      TrailingDoubleQuoteRegEx: /"$/,
      EscapedQuoteOrBackslashGlobalRegEx: /\\(["\\])/g,
      TrailingStarsRegEx: /\*+$/,
      // A filter's values in the address bar (the Logs tab's js/logQuery.js). They are joined
      // with commas, so a comma inside a value is written as its escape, and so is the percent
      // sign an escape starts with. The two pairs undo one another.
      PercentGlobalRegEx: /%/g,
      CommaGlobalRegEx: /,/g,
      EscapedPercentGlobalRegEx: /%25/g,
      EscapedCommaGlobalRegEx: /%2C/gi,
      // The values two of the Logs tab's typed filters take: an HTTP status as a class ("5xx")
      // or a code ("503"), and a least duration (">500ms", ">=1.5s", ">2m").
      HttpStatusClassRegEx: /^[1-5]xx$/,
      HttpStatusFilterValueRegEx: /^[1-5](xx|\d{2})$/,
      LeastDurationFilterValueRegEx: /^>=?(\d+(?:\.\d+)?)(ms|s|m)?$/,
    },
  },
  DateTimeSettings: {
    Format: {
      DateFormat: "MM/DD/YYYY",
      TimeFormat: "HH:mm",
      DateTimeFormat: "MM/DD/YYYY HH:mm",
    },
    Component: {
      DateOnly: "DateOnly",
      TimeOnly: "TimeOnly",
      FullDateTime: "FullDateTime",
    },
  },
  ErrorCodes: {
    SendEmailFailed: "SendEmailFailed",
    GeneralFailure: "Failed",
    AccessDenied: "Access Denied",
    Identity: {
      ExternalLoginFailedGeneralCode: "ExternalLoginFailedGeneral",
      LocalLoginFailedGeneralCode: "LocalLoginFailedGeneral",
      InvalidCredentials: "InvalidCredentials",
      UserDisabledCode: "UserDisabled",
      DuplicateUserCode: "DuplicateEmailHandle",
    },
  },
  ComponentCloseMode: {
    Event: "Event",
    Redirect: "Redirect",
  },
  MessageTypes: {
    Error: "Error",
    Success: "Success",
  },
  OperationResult: {
    Succeeded: "Succeeded",
    Failed: "Failed",
    Unknown: "Unknown",
    Canceled: "Canceled",
    Partial: "Partial",
  },
  OperationState: {
    Pending: "Pending",
    Completed: "Completed",
    Queued: "Queued",
    InProgress: "InProgress",
    Rejected: "Rejected",
    // "Awaiting" — parked on a human decision (e.g. GH env approval reviewer).
    // Kept distinct from Pending (pre-execution) and InProgress (machine actively
    // running) so the Portal can render it as "needs a human".
    Awaiting: "Awaiting",
  },
  FormStatus: {
    Loading: {
      Status: "loading",
      Message: "Loading...",
    },
    Loaded: {
      Status: "loaded",
      Message: "Finished.",
    },
  },
  GUID: "00000000-0000-0000-0000-000000000000",
  Environment: {
    Dev: {
      Code: "development",
      Name: "Development",
      SortOrder: 0,
    },
    QualityAssurance: {
      Code: "qualityassurance",
      Name: "Quality Assurance",
      SortOrder: 1,
    },
    Staging: {
      Code: "staging",
      Name: "Staging",
      SortOrder: 2,
    },
    Production: {
      Code: "production",
      Name: "Production",
      SortOrder: 3,
    },
  },
  Environments: [
    {
      key: "development",
      text: "Development",
      value: "development",
    },
    {
      key: "qualityassurance",
      text: "Quality Assurance",
      value: "qualityassurance",
    },
    {
      key: "staging",
      text: "Staging",
      value: "staging",
    },
    {
      key: "production",
      text: "Production",
      value: "production",
    },
  ],
  //To be replaced by the email in Contact Details
  PellerexEmailAddress: "info@pellerex.com",
  ContactDetails: {
    LinkedInPage: "https://www.linkedin.com/company/pellerex",
    TwitterHandle: "pellerex",
    Email: "info@pellerex.com",
    SupportEmailAddress: "support@pellerex.com",
  },
  ChatContext: {
    ChangeRequests: "ChangeRequests",
    DirectMessages: "DirectMessage",
  },
  AddressMode: {
    ManualEntry: "manualEntry",
  },
  ValueCollections: {
    YesNoUnsure: [
      {
        key: "yes",
        text: "Yes",
        value: "yes",
        description: "",
      },
      {
        key: "no",
        text: "No",
        value: "no",
        description: "",
      },
      {
        key: "unsure",
        text: "Unsure",
        value: "unsure",
        description: "",
      },
    ],
    FrontendStack: [
      {
        key: "react",
        text: "React",
        value: "react",
        description: "",
      },
      {
        key: "angular",
        text: "Angular",
        value: "angular",
        description: "",
      },
      {
        key: "vue",
        text: "Vue",
        value: "vue",
        description: "",
      },
      {
        key: "blazor",
        text: "Blazor",
        value: "blazor",
        description: "",
      },
    ],
    BackendStack: [
      {
        key: "php",
        text: "PHP",
        value: "php",
        description: "",
      },
      {
        key: ".net",
        text: ".NET",
        value: ".net",
        description: "",
      },
      {
        key: "python",
        text: "Python",
        value: "python",
        description: "",
      },
      {
        key: "java",
        text: "Java",
        value: "java",
        description: "",
      },
      {
        key: "django",
        text: "Django",
        value: "django",
        description: "",
      },
      {
        key: "nodejs",
        text: "Node.js",
        value: "nodejs",
        description: "",
      },
      {
        key: "laravel",
        text: "Laravel",
        value: "laravel",
        description: "",
      },
    ],
    DatabaseTool: [
      {
        key: "sqlserver",
        text: "Microsoft SQL Server",
        value: "sqlserver",
        description: "",
      },
      {
        key: "postgresql",
        text: "PostgreSQL",
        value: "postgresql",
        description: "",
      },
      {
        key: "mysql",
        text: "MySQL",
        value: "mysql",
        description: "",
      },
      {
        key: "mongodb",
        text: "MongoDB",
        value: "mongodb",
        description: "",
      },
      {
        key: "dynamodb",
        text: "DynamoDB",
        value: "dynamodb",
        description: "",
      },
      {
        key: "oracle",
        text: "Oracle",
        value: "oracle",
        description: "",
      },
      {
        key: "db2",
        text: "DB2",
        value: "db2",
        description: "",
      },
    ],
    HostingOptions: [
      {
        key: "app-services",
        text: "App Services",
        value: "app-services",
        description: "",
      },
      {
        key: "containerised",
        text: "Containerised",
        value: "containerised",
        description: "",
      },
      {
        key: "serverless",
        text: "Serverless",
        value: "serverless",
        description: "",
      },
    ],
    InfrastructureOptions: [
      {
        key: "aws",
        text: "AWS",
        value: "aws",
        description: "",
      },
      {
        key: "azure",
        text: "Azure",
        value: "azure",
        description: "",
      },
      {
        key: "gcp",
        text: "Google Cloud",
        value: "gcp",
        description: "",
      },
      {
        key: "oracle",
        text: "Oracle Cloud",
        value: "oracle",
        description: "",
      },
    ],
  },
  Piloting: {
    SegmentNames: {
      NewFeatures: "new-features",
      Mcp: "mcp",
    },
    Segments: {
      NewFeatures: {
        AllowedUsers: ["m@pellerex.com"],
      },
      Mcp: {
        AllowedUsers: ["m@panteya.com.au"],
      },
    },
  },
  Notification: {
    Types: {
      Success: "success",
      Error: "error",
      Warning: "warning",
    },
  },
  StatusCodes: {
    OK: 200,
    Accepted: 202,
    InternalServerError: 500,
    BadGateway: 502,
    NotFound: 404,
    BadRequest: 400,
    Unauthorized: 401,
    Forbidden: 403,
    Conflict: 409,
    TooManyRequests: 429,
  },
  // The HTTP request methods, uppercase as HTTP writes them.
  HttpMethods: ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"],
  // Request headers the platform's services read by name.
  Headers: {
    // One id for a request and everything done for it, on every service's log lines. The portal is
    // where a user's request starts, so it creates the id; the services carry it on.
    CorrelationId: "X-Correlation-Id",
  },
  // components/LogTerminal.
  LogTerminal: {
    // Within this many pixels of the bottom the view counts as following new lines.
    FollowThresholdPx: 24,
    // Words a log line writes just before a response status code ("responded 200", "StatusCode: 404"),
    // so the terminal colours that code and leaves other numbers alone.
    StatusCodeLeadWords: ["responded", "returned", "status", "Status", "StatusCode", "statusCode"],
  },
  // What the platform's redaction writes in place of what it removed, the same values as CommonLayer
  // Constants.Redaction, so a log viewer can show the gaps as gaps.
  Redaction: {
    Secret: "***redacted***",
    InternalIp: "[internal-ip]",
    InternalHost: "[internal-host]",
    Node: "[node]",
    CloudResource: "[cloud-resource]",
    Image: "[image]",
    Truncated: "[truncated]",
  },
  // Server-Sent Events, read over fetch so the request can carry the bearer token.
  EventStream: {
    ContentType: "text/event-stream",
    // The event type of a frame that names none.
    DefaultEventType: "message",
    // The frame fields the portal reads; id and retry are not used.
    Fields: {
      Data: "data",
      Event: "event",
    },
  },
  DevOps: {
    Pipelines: {
      NoCodeChange: "No Code Change",
      Release: {
        Action: {
          Approved: "approved",
          Rejected: "rejected",
        },
      },
    },
  },
  Messaging: {
    Scope: {
      InterBrowser: "inter-browser",
    },
    Type: {
      AuthenticationSucceeded: "authentication-succeeded",
      AuthenticationFailed: "authentication-failed",
      ShouldRefreshThePage: "refresh-the-page",
    },
  },
  Input: {
    Keys: {
      Escape: "Escape",
      // With the command key (Ctrl elsewhere): opens the dashboard's search
      OpenSearch: "k",
      // Puts the cursor in the page's search box (components/Search/FilterSearch)
      FocusSearch: "/",
    },
  },
};

export const Dashboard = {
  Commands: {
    ChangeApp: "change-app",
    ShowAppSpaceOverview: "show-app-space-overview",
    ShowServiceInstance: "general:show-service-instance",
    ShowApi: "show-api",
    ShowModel: "show-model",
    ShowInbox: "show-inbox",
    ShowProfile: "show-profile",
    ShowBillingDetails: "show-billing-details",
    ShowSubscriptions: "show-subscriptions",
    ShowTenantManagement: "show-tenant-management",
    ShowOrganisationManagement: "show-organisation-management",
    RefreshDashboard: "refresh-dashboard",
    ManageAppSpace: "manage-app-space",
    ShowHealthAndCompliance: "show-health-and-compliance",
    ShowJobOrchestrator: "show-job-orchestrator",
    ShowNamingConfirmation: "show-naming-confirmation",
    ShowCreateService: "show-create-service",
    OpenDevelop: "open-develop",       // Opens command palette pre-filtered with the user's apps for Day 1 (Develop) workflows
    OpenGovern: "open-govern",         // Opens command palette pre-filtered with health/governance actions for Day 2 (Govern) workflows
    CreateManagedApi: "create-managed-api",
    CreateManagedApp: "create-managed-app",
    CreateManagedMcp: "create-managed-mcp",
    ResetPassword: "reset-password",
  },
  Analytics: {
    // The periods a chart or a figure covers, spelled as CommonLayer's TimeRange spells them for
    // every service (decided 2026-11-03).
    TimeRanges: {
      Last30Minutes: "last-30-minutes",
      Last1Hour: "last-1-hour",
      Last6Hours: "last-6-hours",
      Last12Hours: "last-12-hours",
      Last24Hours: "last-24-hours",
      Last7Days: "last-7-days",
      Last30Days: "last-30-days",
      Last90Days: "last-90-days",
      Last180Days: "last-180-days",
      Last365Days: "last-365-days",
    },
    Levels: {
      Tenant: "Tenant",
      Organisation: "Organisation",
      AppSpace: "AppSpace",
      Product: "Product",
    },
  },
};
